<template>
  <div
    v-if="uniqueServiceTypeFilters.length > 1"
    class="rate-shop-service-type-filters"
  >
    <ChipButton
      v-for="serviceType in uniqueServiceTypeFilters"
      :key="serviceType"
      v-model="internalValue[serviceType]"
      :label="filterLabel(serviceType)"
      :count="filterCount(serviceType)"
      :severity="
        serviceType === WellKnownRateStrategy.EXCLUDED ||
        serviceType === WellKnownRateStrategy.RATE_ERROR
          ? Severity.DANGER
          : undefined
      "
    />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue } from '@rygen/rygen-ui/composables';
import { Loadable, Severity } from '@rygen/rygen-ui/model';

import { useRateShop, useRoutingGuide } from '@/composables';
import {
  RateShop,
  type ServiceTypeFilters,
  WellKnownRateStrategy,
} from '@/model';

import ChipButton from '@rygen/rygen-ui/components/button/ChipButton.vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<ServiceTypeFilters>,
    default: () => ({}),
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
});
const { modelValue, rateShop } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: Record<string, boolean>): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts) => {
    const entries = Object.entries(opts).map(([key, isSelected]) => [
      key,
      isSelected ?? false,
    ]);

    return Object.fromEntries(entries);
  },
});
const { rateErrors, serviceTypeFilterCounts, uniqueServiceTypeFilters } =
  useRateShop(rateShop, { serviceTypeFilters: internalValue });
const { excludedRates } = useRoutingGuide(rateShop, internalValue);

const filterLabel = (serviceType: string) => {
  if (serviceType === WellKnownRateStrategy.EXCLUDED) {
    return t('rateShop.excluded');
  }
  return serviceType === WellKnownRateStrategy.RATE_ERROR
    ? t('rateShop.unrated')
    : serviceType;
};

const filterCount = (serviceType: string) => {
  if (serviceType === WellKnownRateStrategy.EXCLUDED) {
    return excludedRates.value.length;
  }
  return serviceType === WellKnownRateStrategy.RATE_ERROR
    ? rateErrors.value.length
    : serviceTypeFilterCounts.value[serviceType];
};
</script>

<style>
.rate-shop-service-type-filters {
  display: flex;
  gap: var(--sp-3);
  align-items: center;
  flex: 1 0 auto;
  flex-wrap: wrap;
}
</style>
